import React from 'react';
import styled, {keyframes} from 'styled-components';

const shimmer = keyframes`
    0% {
        background-position: -468px 0;
    }
    100% {
        background-position: 468px 0;
    }
`;

const SkeletonWrapper = styled.div`
    padding-top: ${props => props.isMobile ? '10vh' : '70px'};
    max-width: 1400px;
    margin: 0 auto;
`;

const BreadcrumbSkeleton = styled.div`
    width: 100%;
    padding: 15px 20px;
    display: flex;
    gap: 10px;
    align-items: center;
`;

const BreadcrumbButton = styled.div`
    width: 54px;
    height: 42px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 4px;
`;

const BreadcrumbItem = styled.div`
    width: ${props => props.width || '80px'};
    height: 20px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 4px;
`;

const ProductContainer = styled.div`
    display: grid;
    grid-template-columns: ${props => props.isMobile ? '1fr' : '500px 1fr'};
    gap: 30px;
    padding: 20px;

    @media (max-width: 990px) {
        grid-template-columns: 1fr;
    }
`;

const ProductImageSection = styled.div`
    display: flex;
    gap: 15px;
`;

const ImageSliderColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 80px;

    @media (max-width: 768px) {
        display: none;
    }
`;

const SliderImageBox = styled.div`
    width: 80px;
    height: 80px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 8px;
`;

const MainImageBox = styled.div`
    flex: 1;
    height: 400px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 8px;

    @media (max-width: 768px) {
        height: 300px;
    }
`;

const ProductInfoSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const TitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 15px;
`;

const TitleBox = styled.div`
    flex: 1;
    height: 32px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 4px;
`;

const BrandBox = styled.div`
    width: 120px;
    height: 28px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 4px;
`;

const PriceRow = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
`;

const PriceBox = styled.div`
    width: ${props => props.width || '100px'};
    height: 36px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 4px;
`;

const FavoriteBox = styled.div`
    width: 50px;
    height: 50px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 50%;
    margin-left: auto;
`;

const BadgeBox = styled.div`
    width: 150px;
    height: 32px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 4px;
`;

const MetaRow = styled.div`
    display: flex;
    gap: 20px;
    align-items: center;
`;

const MetaBox = styled.div`
    width: ${props => props.width || '60px'};
    height: 24px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 4px;
`;

const DescriptionBox = styled.div`
    width: 100%;
    height: ${props => props.height || '80px'};
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 4px;
    margin-top: 10px;
`;

const QuantityBox = styled.div`
    width: 180px;
    height: 48px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 25px;
`;

const ButtonBox = styled.div`
    width: 200px;
    height: 48px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 25px;
`;

const RelatedProductsSection = styled.div`
    margin-top: 40px;
    padding: 20px;
`;

const SectionTitle = styled.div`
    width: 200px;
    height: 28px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 4px;
    margin-bottom: 20px;
`;

const ProductGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 20px;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const ProductCardBox = styled.div`
    width: 100%;
    height: 320px;
    background: linear-gradient(to right, #f0f0f0 8%, #f8f8f8 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.2s ease-in-out infinite;
    border-radius: 8px;
`;

const ProductDetailSkeleton = ({isMobile = false}) => {
	return (
		<SkeletonWrapper isMobile={isMobile}>
			{/* Breadcrumb Skeleton */}
			<BreadcrumbSkeleton>
				<BreadcrumbButton/>
				<BreadcrumbItem width="60px"/>
				<BreadcrumbItem width="120px"/>
			</BreadcrumbSkeleton>

			{/* Product Detail Section */}
			<ProductContainer isMobile={isMobile}>
				{/* Left: Images */}
				<ProductImageSection>
					{!isMobile && (
						<ImageSliderColumn>
							<SliderImageBox/>
							<SliderImageBox/>
							<SliderImageBox/>
						</ImageSliderColumn>
					)}
					<MainImageBox/>
				</ProductImageSection>

				{/* Right: Product Info */}
				<ProductInfoSection>
					{/* Title + Brand */}
					<TitleRow>
						<div style={{flex: 1}}>
							<TitleBox/>
						</div>
					</TitleRow>
					<BrandBox/>

					{/* Price + Favorite */}
					<PriceRow>
						<PriceBox width="120px"/>
						<PriceBox width="140px"/>
						<FavoriteBox/>
					</PriceRow>

					{/* Delivery Badge */}
					<BadgeBox/>

					{/* Meta Info (Country, Weight, etc.) */}
					<MetaRow>
						<MetaBox width="40px"/>
						<MetaBox width="80px"/>
						<MetaBox width="100px"/>
					</MetaRow>

					{/* Description */}
					<DescriptionBox height="100px"/>

					{/* Quantity + Add to Cart */}
					<div style={{display: 'flex', gap: '15px', marginTop: '20px'}}>
						<QuantityBox/>
						<ButtonBox/>
					</div>

					{/* Additional Info */}
					<DescriptionBox height="60px"/>
				</ProductInfoSection>
			</ProductContainer>

			{/* Related Products Section */}
			<RelatedProductsSection>
				<SectionTitle/>
				<ProductGrid>
					<ProductCardBox/>
					<ProductCardBox/>
					<ProductCardBox/>
					<ProductCardBox/>
				</ProductGrid>
			</RelatedProductsSection>
		</SkeletonWrapper>
	);
};

export default ProductDetailSkeleton;
